import { Controller } from '@hotwired/stimulus'

// Global cache so Quill loads only one time
let quillLibs = null

let last_url_called = null
let last_html = { url: '', html: '' }
globalThis.__linkPreviewInFlight ||= {}

// Register custom emoji blot after Quill is loaded
function registerEmojiBlot(Quill) {
  const Embed = Quill.import('blots/embed')

  class EmojiBlot extends Embed {
    static create(value) {
      const node = super.create()

      node.dataset.emoji = value.unified
      node.dataset.native = value.native || ''
      node.dataset.url = value.url

      Object.assign(node.style, {
        display: 'inline-block',
        width: '20px',
        height: '20px',
        lineHeight: '20px',
        verticalAlign: 'middle',
        textAlign: 'center',
        position: 'relative'
      })

      const textSpan = document.createElement('span')
      textSpan.textContent = value.native || '🙂'
      node.appendChild(textSpan)

      if (value.url) {
        const img = new Image()

        img.onload = () => {
          node.style.backgroundImage = `url(${value.url})`
          node.style.backgroundSize = '20px 20px'
          node.style.backgroundRepeat = 'no-repeat'
          node.style.backgroundPosition = 'center'
          textSpan.classList.add('opacity-0')

          node
            .closest('.ql-editor')
            ?.dispatchEvent(
              new CustomEvent('emoji-image-loaded', { bubbles: true })
            )
        }

        img.onerror = () => {
          textSpan.style.color = ''
        }

        img.src = value.url
      }

      return node
    }

    static value(node) {
      return {
        unified: node.dataset.emoji,
        native: node.dataset.native || '',
        url: node.dataset.url
      }
    }
  }

  EmojiBlot.blotName = 'emoji'
  EmojiBlot.tagName = 'span'
  EmojiBlot.className = 'ql-emoji'

  Quill.register(EmojiBlot, true)
}

export default class QuillController extends Controller {
  static targets = ['createPost']

  // Load heavy libraries dynamically
  async loadLibs() {
    if (quillLibs) return quillLibs

    const QuillModule = await import('quill')
    const TributeModule = await import('tributejs')

    const Quill = QuillModule.default || QuillModule.Quill || QuillModule
    const Tribute = TributeModule.default || TributeModule

    registerEmojiBlot(Quill)

    quillLibs = { Quill, Tribute }
    return quillLibs
  }

  async connect() {
    const { Quill, Tribute } = await this.loadLibs()

    this.Quill = Quill
    this.Tribute = Tribute
    this.index = 0

    this.configureQuillFormats()

    const element = this.element
    const dataName = element.dataset.name
    const toolbarConfig = this.toolbarConfig(element, dataName)

    const quill = new Quill(element, {
      theme: 'snow',
      placeholder: element.dataset.placeholder || '',
      modules: {
        toolbar: toolbarConfig
      }
    })

    this.quill = quill
    quill.root.addEventListener(
      'drop',
      (event) => {
        const uri = event.dataTransfer?.getData('text/uri-list')

        const text = event.dataTransfer?.getData('text/plain')

        const url = uri || text

        if (!url || !/^https?:\/\//i.test(url)) {
          return
        }

        event.preventDefault()
        event.stopPropagation()

        const range = quill.getSelection(true) || {
          index: quill.getLength(),
          length: 0
        }

        quill.insertText(
          range.index,
          url,
          {
            link: url
          },
          'user'
        )

        quill.setSelection(range.index + url.length, 0, 'silent')

        setTimeout(() => {
          handleLinks(quill, this.element)
        }, 100)
      },
      true
    )
    quill.blur()
    quill.root.querySelectorAll('a.tribute-inserted').forEach((link) => {
      link.classList.add('align-items-center', 'text-primary')
      link.dataset.mention = 'true'
      link.dataset.name = link.textContent.trim()
      link.setAttribute('target', '_blank')
    })

    this.dispatchProgressUpdate(quill)
    this.setupLinkSanitizer()
    this.setupHiddenInput(element, dataName)
    this.setupKeyboard(quill)
    this.setupCreatePostEvents(quill)
    this.setupTextChange(quill)
    this.setupEmojiEvents(quill)
    this.focusEditor(quill)
  }

  // Quill format config
  configureQuillFormats() {
    const Quill = this.Quill

    const fontSizeArr = [
      '8px',
      '9px',
      '10px',
      '12px',
      '14px',
      '16px',
      '20px',
      '24px',
      '32px',
      '42px',
      '54px',
      '68px',
      '84px',
      '98px'
    ]

    const fontFamilyArr = [
      '',
      'sans-serif',
      'aboreto',
      'montserrat',
      'roboto',
      'explora',
      'times-new-roman',
      'arial'
    ]

    const Size = Quill.import('attributors/style/size')
    Size.whitelist = fontSizeArr
    Quill.register(Size, true)

    const Font = Quill.import('formats/font')
    Font.whitelist = fontFamilyArr
    Quill.register(Font, true)

    const BackgroundClass = Quill.import('attributors/class/background')
    const ColorClass = Quill.import('attributors/class/color')

    Quill.register(BackgroundClass, true)
    Quill.register(ColorClass, true)
  }

  // Toolbar config
  toolbarConfig(element, dataName) {
    const toolbar = element.dataset.toolbar

    if (toolbar !== undefined && toolbar !== 'true') return false

    if (dataName === 'social_comment[comment]') {
      return 'emoji'
    }

    return [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'emoji']
    ]
  }

  // Link sanitizer
  setupLinkSanitizer() {
    const Link = this.Quill.import('formats/link')
    const builtInFunc = Link.sanitize

    Link.sanitize = function customSanitizeLinkInput(linkValueInput) {
      let val = linkValueInput

      if (!/^\w+:/.test(val) && !/^https?:/.test(val)) {
        val = 'http:' + val
      }

      return builtInFunc.call(this, val)
    }
  }

  // Hidden input for form submit
  setupHiddenInput(element, dataName) {
    const hiddenInput = document.createElement('input')

    hiddenInput.type = 'hidden'
    hiddenInput.id =
      element.dataset.first === 'true' && element.dataset.id !== ''
        ? element.dataset.id
        : 'not_ignore'
    hiddenInput.name = dataName
    hiddenInput.value = element.dataset.value ?? ''
    hiddenInput.required = element.dataset.required != null
    let otherAttr = element.dataset.hiddenInputAttrs
    if (otherAttr) {
      try {
        otherAttr = JSON.parse(otherAttr)

        Object.entries(otherAttr).forEach(([key, value]) => {
          hiddenInput.setAttribute(key, value)
        })
      } catch (e) {
        console.error('Invalid JSON in data-hidden-input-attrs', e)
      }
    }
    this.hiddenInput = hiddenInput
    element.parentElement.appendChild(hiddenInput)
  }

  setupKeyboard(quill) {
    quill.keyboard.addBinding({ key: 13 }, () => {
      this.element.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'Enter', bubbles: true })
      )
      return false
    })
  }

  setupCreatePostEvents(quill) {
    if (!this.hasCreatePostTarget) return

    $(this.element).on('click.quillController', () => {
      if ($(this.element).closest('#addPostModal').length > 0) {
        $('.post_media').addClass('d-none')
        $('#post-media-' + $(this.element).attr('data-index'))
          .parent()
          .removeClass('d-none')
      }
    })

    $(this.element).on('keyup.quillController', (event) => {
      if (this.characterLength(quill) < 1) {
        $('.post_submit').addClass('disabled')
        $('.add_post,.add_child_post,.progress-ring').addClass('d-none')
      } else {
        $('.post_submit').removeClass('disabled')
        $('.add_post,.add_child_post,.progress-ring').removeClass('d-none')
      }

      if (event.which === 13) {
        event.preventDefault()
        return false
      }
    })

    this.mentionUser(quill)
  }

  setupTextChange(quill) {
    const element = this.element
    const hiddenInput = this.hiddenInput
    const dataName = element.dataset.name
    const limit = Number(element.dataset.limit || 0)
    const bodyCount = $('.' + element.dataset.counterClass).find('.body-count')

    postSubmit(hiddenInput, quill)

    quill.on('text-change', (delta, oldDelta, source) => {
      if (this.isConverting) return

      if (source === 'user') {
        this.convertTextToEmoji(quill, delta)
        this.autoLinkUrls(quill)
      }

      let html = quill.root.innerHTML
      const length = this.characterLength(quill)

      postSubmit(hiddenInput, quill)

      this.dispatchProgressUpdate(quill)

      setTimeout(
        function () {
          if (!this.isPasting) {
            handleLinks(quill, element)
          }

          html = quill.root.innerHTML.trim()
          html = html.replace(
            /^(?:\s*<p>(?:\s*|<br\s*\/?>)*<\/p>)+|(?:\s*<p>(?:\s*|<br\s*\/?>)*<\/p>)+$/gi,
            ''
          )
          html = html.replace(/\s+/g, ' ').trim()
          hiddenInput.value = html
          // Revalidate after Quill updates the hidden input value (fixes Android typing issue)
          requestAnimationFrame(() => {
            // when user remove all text error still alive
            const field = $(hiddenInput)
            const isPlaceholderVisible =
              quill.root.classList.contains('ql-blank')

            if (isPlaceholderVisible) {
              field.removeClass('error')
              field.next('label.error').remove()
              return
            }
            $(hiddenInput).valid()
          })
          hiddenInput.dispatchEvent(new Event('input', { bubbles: true }))
          $(hiddenInput).trigger('keyup')

          const images = this.element.querySelectorAll('img')

          if (images.length > 0) {
            images.forEach((img) => {
              if (
                $(img).attr('class') === undefined &&
                !$(img).attr('src').startsWith('https://media.fatsu.com')
              ) {
                $(img).remove()
              }
            })
          }
        }.bind(this),
        0
      )
    })

    quill.on('selection-change', (range) => {
      if (range) this.index = range.index
    })
  }

  setupEmojiEvents(quill) {
    quill.root.addEventListener('emoji-image-loaded', () => {
      $(this.element).trigger('keyup')
      this.hiddenInput.value = quill.root.innerHTML
    })

    quill.clipboard.addMatcher(Node.TEXT_NODE, (node, delta) => {
      const ops = []

      delta.ops.forEach((op) => {
        if (typeof op.insert === 'string') {
          this.segments(op.insert).forEach((char) => {
            if (isEmojiChar(char)) {
              const unified = getUnified(char)

              ops.push({
                insert: {
                  emoji: {
                    unified,
                    native: char,
                    url: emojiUrl(unified)
                  }
                }
              })
            } else {
              ops.push({ insert: char })
            }
          })
        }
      })

      delta.ops = ops
      return delta
    })

    quill.root.addEventListener('mousedown', (e) => {
      const blotNode = e.target.closest('.ql-emoji')
      if (!blotNode) return

      e.preventDefault()

      const blot = this.Quill.find(blotNode)
      const index = quill.getIndex(blot)

      quill.setSelection(index + 1, 0, 'silent')
      quill.focus()
    })
  }

  focusEditor(quill) {
    setTimeout(() => {
      if (
        globalThis.innerWidth > 576 &&
        this.element.dataset.focus !== undefined
      ) {
        quill.focus({ preventScroll: true })
      }
    }, 500)
  }

  convertTextToEmoji(quill, delta) {
    let indexTracker = 0

    delta.ops.forEach((op) => {
      if (op.retain) {
        indexTracker += op.retain
        return
      }

      if (!(op.insert && typeof op.insert === 'string')) return

      const chars = this.mergeZWJSequences(this.segments(op.insert))

      chars.forEach((char) => {
        if (isEmojiChar(char)) {
          this.isConverting = true

          const unified = getUnified(char)
          const startIndex = indexTracker

          try {
            quill.deleteText(startIndex, char.length, 'silent')

            quill.insertEmbed(
              startIndex,
              'emoji',
              {
                unified,
                native: char,
                url: emojiUrl(unified)
              },
              'silent'
            )

            quill.setSelection(startIndex + 1, 0, 'silent')
            quill.focus()

            requestAnimationFrame(() => {
              quill.setSelection(startIndex + 1, 0, 'silent')
            })
          } finally {
            this.isConverting = false
          }
        }

        indexTracker += char.length
      })
    })
  }

  mergeZWJSequences(graphemes) {
    const result = []
    let buffer = ''

    graphemes.forEach((g, i) => {
      buffer += g
      const next = graphemes[i + 1]

      if (next === '\u200d' || g.includes('\u200d')) return

      result.push(buffer)
      buffer = ''
    })

    if (buffer) result.push(buffer)

    return result
  }

  mentionUser(quill) {
    this.tribute = new this.Tribute({
      collection: [
        {
          trigger: '@',
          allowSpaces: true,
          selectTemplate: (item) => item?.original?.display_name || null,
          menuItemTemplate: (item) => item.original.name,
          lookup: 'display_name',
          values: (query, callback) => {
            this.debounce(() => {
              fetch(`/posts/get_mention_users?query=${query}`)
                .then((response) => response.json())
                .then((data) => callback(data || []))
                .catch(() => callback([]))
            }, 400)()
          }
        },
        {
          trigger: '#',
          allowSpaces: true,
          selectTemplate: (item) => item?.original?.display_name || null,
          menuItemTemplate: (item) => item.original.name,
          lookup: 'name',
          values: (query, callback) => {
            if (query.length <= 0) return

            this.debounce(() => {
              fetch(`/posts/get_hashtag?query=${query}`)
                .then((response) => response.json())
                .then((data) => callback(data || []))
                .catch(() => callback([]))
            }, 400)()
          }
        }
      ]
    })

    this.tribute.attach(quill.root)
  }

  hideOpenEmojiPicker() {
    const openDropdown = document.querySelector(
      '[data-controller="emoji-mart-dropdown"]:has([data-emoji-mart-dropdown-target="menu"]:not(.d-none))'
    )

    openDropdown?.dispatchEvent(
      new CustomEvent('emoji-mart-dropdown:hide', { bubbles: true })
    )
  }

  debounce(func, delay) {
    let timer

    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => func(...args), delay)
    }
  }

  characterLength(quill) {
    if (!quill) return 0

    const text = this.htmlToText(quill.root.innerHTML).trim()
    return this.segments(text).length
  }

  htmlToText(html) {
    const div = document.createElement('div')
    div.innerHTML = html

    div
      .querySelectorAll('span[data-native], span[data-emoji]')
      .forEach((node) => {
        node.replaceWith(document.createTextNode(node.dataset.native || ''))
      })

    div.querySelectorAll('img').forEach((img) => {
      img.replaceWith(document.createTextNode('◉'))
    })

    div.querySelectorAll('br').forEach((br) => br.replaceWith('\n'))

    div.querySelectorAll('p, div, li').forEach((node) => {
      if (!node.textContent?.endsWith('\n')) {
        node.append(document.createTextNode('\n'))
      }
    })

    return (div.textContent || '').replace(/\u00A0/g, ' ').replace(/\n+$/g, '')
  }

  segments(text) {
    if (!text) return []

    if (!this._segmenter) {
      this._segmenter = new Intl.Segmenter(undefined, {
        granularity: 'grapheme'
      })
    }

    return [...this._segmenter.segment(text)].map((p) => p.segment)
  }

  dispatchProgressUpdate(quill) {
    this.element.dispatchEvent(
      new CustomEvent('quill:progress', {
        bubbles: true,
        detail: {
          length: this.characterLength(quill),
          max: 280
        }
      })
    )
  }

  clear() {
    if (!this.quill) return

    this.quill.setContents([])

    if (this.hiddenInput) {
      this.hiddenInput.value = ''
      this.hiddenInput.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  disconnect() {
    $(this.element).off('.quillController')
    this.tribute?.detach?.(this.quill?.root)
    this.quill = null
  }

  autoLinkUrls(quill) {
    const text = quill.getText()
    const urlRegex = /(https?:\/\/[^\s]+)/g

    let match

    while ((match = urlRegex.exec(text)) !== null) {
      const url = match[0]
      const index = match.index

      const formats = quill.getFormat(index, url.length)

      if (!formats.link) {
        quill.formatText(
          index,
          url.length,
          {
            link: url
          },
          'silent'
        )
      }
    }
  }
}

// Enable/disable submit buttons
function postSubmit(hiddenInput, quill) {
  if (hiddenInput.name === 'post[description]') {
    $('.submit-post').toggleClass('disabled', quill.getLength() <= 1)
  }

  if (hiddenInput.name === 'comment[description]') {
    $('.text_description').toggleClass('disabled', quill.getLength() <= 1)

    if (hiddenInput.value === 'undefined') {
      hiddenInput.value = ''
    }
  }
}

async function createThumb(url, element) {
  if (
    last_url_called == url.href &&
    last_html['html'] != '' &&
    last_html['url'] == last_url_called
  ) {
    // $(element).closest('form').find('#thumb_url').val(url.href)
    // $(element).closest('form').find('#link_preview').val(true)
    // $(element).closest('form').find('#link-thumb').html(last_html['html'])
    return
  } else {
    last_url_called = url.href
  }
  var isMessageForm = $(element).data('name') === 'message[body]'
  try {
    let metaInformation

    if (globalThis.__linkPreviewInFlight[url.href]) {
      metaInformation = await globalThis.__linkPreviewInFlight[url.href]
    } else {
      globalThis.__linkPreviewInFlight[url.href] = fetch('/link_preview', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: url.href })
      })
        .then((res) => res.json())
        .finally(() => {
          delete globalThis.__linkPreviewInFlight[url.href]
        })
      metaInformation = await globalThis.__linkPreviewInFlight[url.href]
    }

    if (
      !metaInformation ||
      metaInformation['title'] === 'Error' ||
      $(element).closest('form').find('#thumb_url').val() === ''
    )
      return

    if (isMessageForm) {
      const linksInput = $(element).closest('form').find('#message_links_data')

      let imageLinks = []
      try {
        imageLinks = JSON.parse(linksInput.val() || '[]')
      } catch (e) {
        imageLinks = []
      }

      const alreadyAdded = imageLinks.some((l) => l.url === url.href)
      if (alreadyAdded) return

      imageLinks.push({
        url: url.href,
        image: metaInformation.images || null,
        title: metaInformation.title || '',
        description: metaInformation.description || ''
      })

      linksInput.val(JSON.stringify(imageLinks))
      return
    } else if (!window.location.pathname.includes('messages')) {
      const commonHtml =
        "<div class='preview-link mt-12 position-relative overflow-hidden w-100'>" +
        "<button class='btn p-0 remove-field-button position-absolute top-0 end-0 m-3 z-2' type='button' data-action='strip-post#removePreview'>" +
        "<i class='ri-close-circle-line text-danger fs-22'></i>" +
        '</button>' +
        "<div class='h-lg-300px h-180px position-relative overflow-hidden rounded-3'>" +
        "<img class='d-block object-fit-contain image img-fluid w-100 h-100 rounded-inherit link-image z-1 position-relative' src='" +
        metaInformation['images'] +
        "' " +
        "alt='" +
        metaInformation['title'] +
        "'>" +
        "<img class='d-block object-fit-cover position-absolute top-0 start-0 filter-blur-10 image img-fluid w-100 h-100 rounded-inherit link-image scale-1x2' src='" +
        metaInformation['images'] +
        "' " +
        "alt='" +
        metaInformation['title'] +
        "'></div>" +
        "<div class='preview-link-desc position-absolute d-flex flex-column align-items-baseline justify-content-center w-100 bottom-0 start-0 text-start p-2 z-1'>"
      const conditionalHtml = metaInformation['description']
        ? "<h6 class='card-title empty-tag max-line-1 transparent-dark text-white-paint text-black p-2 rounded-2 fs-18 fw-bold mb-1 lh-base w-100'>" +
          metaInformation['title'] +
          '</h6>' +
          "<p class='card-text empty-tag max-line-1 transparent-dark text-white p-2 rounded-2 w-100'>" +
          metaInformation['description'] +
          '</p>'
        : "<h6 class='card-title empty-tag max-line-1 transparent-dark text-white-paint text-black p-2 rounded-2 fs-18 fw-bold mb-1 lh-base w-100'>" +
          metaInformation['title'] +
          '</h6>'
      const html = commonHtml + conditionalHtml + '</div></div>'
      last_html['url'] = url.href
      last_html['html'] = html
      $(element).closest('form').find('#thumb_url').val(url.href)
      $(element).closest('form').find('#link_preview').val(true)
      $(element).closest('form').find('#link-thumb').html(html)
    } else {
      const commonHtml =
        "<div class='preview-link mt-12 position-relative rounded-3 overflow-hidden bg-black w-100 mb-3'>" +
        "<button class='btn p-0 remove-field-button position-absolute top-0 end-0 m-2 z-2' type='button' data-action='message-preview#removeLinkPreview'>" +
        "<i class='ri-close-circle-line text-danger fs-22'></i>" +
        '</button>' +
        "<div class='d-flex align-items-center justify-content-start gap-3 p-2'>" +
        "<div class='w-110px h-110px position-relative rounded-2 overflow-hidden flex-shrink-0'>" +
        "<img class='d-block object-fit-contain img-fluid w-100 h-100 z-1 position-relative' src='" +
        metaInformation['images'] +
        "' " +
        "alt='" +
        metaInformation['title'] +
        "'>" +
        "<img class='d-block object-fit-cover position-absolute top-0 start-0 filter-blur-5 img-fluid w-100 h-300px scale-1x2' src='" +
        metaInformation['images'] +
        "' " +
        "alt='" +
        metaInformation['title'] +
        "'>" +
        '</div>' +
        "<div class='preview-link-desc d-flex flex-column align-items-baseline justify-content-center w-100 text-start z-1'>"
      const conditionalHtml = metaInformation['description']
        ? "<h6 class='card-title empty-tag max-line-1 text-white-paint text-black fs-18 fw-bold mb-1 lh-base w-100 text-break'>" +
          metaInformation['title'] +
          '</h6>' +
          "<p class='card-text empty-tag max-line-1 text-white w-100 text-break'>" +
          metaInformation['description'] +
          '</p>'
        : "<h6 class='card-title empty-tag max-line-1 text-white-paint text-black fs-18 fw-bold mb-1 lh-base w-100 text-break'>" +
          metaInformation['title'] +
          '</h6>'
      const html = commonHtml + conditionalHtml + '</div></div></div>'
      last_html['url'] = url.href
      last_html['html'] = html
      $(element).closest('form').find('#thumb_url').val(url.href)
      $(element).closest('form').find('#link_preview').val(true)
      $(element).closest('form').find('#link-thumb').html(html)
    }
  } catch (e) {
    console.error(e)
  }
}

function isEmojiChar(char) {
  if (!char) return false

  const isEmoji = char.length > 1 || /\p{Emoji_Presentation}/u.test(char)
  const isNonAsciiSymbol = /\p{Symbol}/u.test(char) && char.charCodeAt(0) > 127
  return (isEmoji || isNonAsciiSymbol) && !/[a-zA-Z0-9]/.test(char)
}

function getUnified(char) {
  return Array.from(char)
    .map((c) => c.codePointAt(0).toString(16))
    .join('-')
}

function emojiUrl(unified) {
  return `https://emoji.itoons.com/emojis/${unified}.png`
}

function formatAndCollectLinks(quill, element) {
  const httpLinks = []
  const links = quill.root.querySelectorAll('a')

  links.forEach((link) => {
    if (
      link.classList.contains('tribute-inserted') ||
      link.classList.contains('atwho-inserted')
    ) {
      return
    }

    link.classList.add('text-primary')

    if (
      ['post[description]', 'post[share_message]'].includes(
        element.getAttribute('data-name')
      )
    ) {
      link.classList.add('post-desc-link')
    }

    httpLinks.push(link)
  })

  return httpLinks
}

function handleLinks(quill, element) {
  const httpLinks = formatAndCollectLinks(quill, element)

  if (httpLinks.length > 0) {
    const urlLink = httpLinks[httpLinks.length - 1]
    $(element).closest('form').find('#thumb_url').val(urlLink.href)
    createThumb(urlLink, element)
  } else {
    // Clear preview after link remove
    clearLinkPreview(element)
  }
}

function clearLinkPreview(element) {
  const form = $(element).closest('form')
  form.find('#thumb_url').val('')
  form.find('#link-thumb').html('')
  form.find('#link_preview').val(false)
}
